import type { BaseRow, Columns, StickyRightCalculations, TableColumn } from '@openx/types/tableHelpers.ts';
import { STICKY_POSITION } from '@openx/types/tableHelpers.ts';
import type { CustomColumnConfig } from './config.ts';
import { ACTION_ICON_CELL } from './config.ts';

export const loadColumnsConfig = <RowT extends BaseRow>(columns: Columns<RowT>, name?: string): Columns<RowT> => {
  if (!name || typeof window === 'undefined') {
    return columns;
  }

  const configKey = `tableHeaderConfig-${name}`;
  const config = localStorage.getItem(configKey);

  if (!config) {
    return columns;
  }

  try {
    const parsedConfig: CustomColumnConfig[] = JSON.parse(config);
    const configMap = new Map(parsedConfig.map(col => [col.key, { hide: col.hide, index: col.index }]));

    // First, apply hide state (skip disabled columns — their visibility is enforced by the source)
    const columnsWithHideState = columns.map(column => {
      const config = configMap.get(column.key);
      if (config?.hide !== undefined && !column.disabled) {
        return { ...column, hide: config.hide };
      }
      return column;
    });

    // Then, sort by saved index if available
    const columnsWithIndex = columnsWithHideState.map(column => {
      const config = configMap.get(column.key);
      return { column, savedIndex: config?.index };
    });

    // Sort columns by saved index, keeping original order for columns without saved index
    const sortedColumns = columnsWithIndex.sort((a, b) => {
      const indexA = a.savedIndex;
      const indexB = b.savedIndex;

      // If both have saved index, sort by index
      if (indexA !== undefined && indexB !== undefined) {
        return indexA - indexB;
      }

      // If only one has saved index, prioritize it
      if (indexA !== undefined) {
        return -1;
      }
      if (indexB !== undefined) {
        return 1;
      }

      // If neither has saved index, keep original order
      return 0;
    });

    return pinFrozenColumns(
      columns,
      sortedColumns.map(({ column }) => column)
    );
  } catch {
    return columns;
  }
};

export const pinFrozenColumns = <RowT extends BaseRow>(
  originalColumns: Columns<RowT>,
  reorderedColumns: Columns<RowT>
): Columns<RowT> => {
  const reorderedByKey = new Map(reorderedColumns.map(column => [column.key, column]));
  const movableColumns = reorderedColumns.filter(column => !column.freeze);

  let movableIndex = 0;

  return originalColumns.map(originalColumn => {
    if (originalColumn.freeze) {
      return reorderedByKey.get(originalColumn.key) ?? originalColumn;
    }

    const nextMovableColumn = movableColumns[movableIndex];
    movableIndex += 1;

    return nextMovableColumn ?? originalColumn;
  });
};

export const groupData = <RowT extends BaseRow>(data: RowT[], groupBy?: keyof RowT): Record<string, RowT[]> => {
  if (!groupBy) return { '': data };
  return data.reduce(
    (acc, item) => {
      const groupKey = String(item[groupBy]) || 'Ungrouped';
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(item);
      return acc;
    },
    {} as Record<string, RowT[]>
  );
};

const parseWidthToPx = (width: string | undefined): number => {
  if (!width) return 0;

  if (width.endsWith('px')) {
    return parseInt(width, 10) || 0;
  }

  if (width.endsWith('%')) {
    return 0;
  }

  return parseInt(width, 10) || 0;
};

export const calculateStickyLeftOffset = <RowT extends BaseRow>(columns: Columns<RowT>, index: number): number => {
  let offset = 0;
  for (let i = 0; i < index; i++) {
    if (columns[i]?.sticky === STICKY_POSITION.LEFT) {
      const width = columns[i].width || columns[i].calcWidth;
      const widthInPx = parseWidthToPx(width);

      if (widthInPx === 0 && columns[i].minWidth) {
        const minWidthInPx = parseWidthToPx(columns[i].minWidth);
        offset += minWidthInPx;
      } else {
        offset += widthInPx;
      }
    }
  }
  return offset;
};

export const calculateStickyRightOffset = <RowT extends BaseRow>(columns: Columns<RowT>): number => {
  let offset = 0;
  for (let i = 0; i < columns.length; i++) {
    if (columns[i]?.sticky === STICKY_POSITION.RIGHT) {
      const width = columns[i].width || columns[i].calcWidth;
      const widthInPx = parseWidthToPx(width);

      if (widthInPx === 0 && columns[i].minWidth) {
        const minWidthInPx = parseWidthToPx(columns[i].minWidth);
        offset += minWidthInPx;
      } else {
        offset += widthInPx;
      }
    }
  }
  return offset;
};

export const calculateStickyRightProps = <RowT extends BaseRow>(
  column: TableColumn<RowT>,
  index: number,
  columns: Columns<RowT>,
  stickyActionColumnOffset?: number
): StickyRightCalculations => {
  if (column.sticky !== STICKY_POSITION.RIGHT) {
    return { isFirstStickyRight: false };
  }

  const stickyRightOffset =
    column.key === ACTION_ICON_CELL && stickyActionColumnOffset !== undefined
      ? 0
      : calculateStickyRightOffset(columns.slice(index + 1));

  const isFirstStickyRight = index === 0 || columns[index - 1]?.sticky !== STICKY_POSITION.RIGHT;

  return {
    stickyRightOffset,
    isFirstStickyRight:
      isFirstStickyRight && (column.key !== ACTION_ICON_CELL || stickyActionColumnOffset !== undefined),
  };
};

export const calculateStickyRightPropsForRow = <RowT extends BaseRow>(
  column: TableColumn<RowT>,
  index: number,
  columns: Columns<RowT>,
  stickyActionColumn?: boolean
): StickyRightCalculations => {
  if (column.sticky !== STICKY_POSITION.RIGHT) {
    return { isFirstStickyRight: false };
  }

  const stickyRightOffset = calculateStickyRightOffset(columns.slice(index + 1)) + (stickyActionColumn ? 68 : 0);

  const isFirstStickyRight =
    column.sticky === STICKY_POSITION.RIGHT && (index === 0 || columns[index - 1]?.sticky !== STICKY_POSITION.RIGHT);

  return {
    stickyRightOffset,
    isFirstStickyRight,
  };
};

const parsePercentage = (width: string | undefined): number => {
  if (!width?.endsWith('%')) return 0;
  return parseFloat(width.replace('%', '')) || 0;
};

export const normalizeColumnWidths = <RowT extends BaseRow>(
  columns: Columns<RowT>,
  originalColumns?: Columns<RowT>
): Columns<RowT> => {
  const columnsToNormalize = originalColumns
    ? (() => {
        const originalWidthsMap = new Map(originalColumns.map(col => [col.key, col.width]));
        return columns.map(col => {
          const originalWidth = originalWidthsMap.get(col.key);
          if (originalWidth) {
            return { ...col, width: originalWidth, calcWidth: undefined };
          }
          return col;
        });
      })()
    : columns;

  const visibleColumns = columnsToNormalize.filter(col => !col.hide);
  const percentageColumns = visibleColumns.filter(col => {
    const width = col.width || col.calcWidth;
    return width?.endsWith('%');
  });

  if (percentageColumns.length === 0) {
    return columnsToNormalize;
  }

  const totalPercentage = percentageColumns.reduce((sum, col) => {
    const width = col.width || col.calcWidth;
    return sum + parsePercentage(width);
  }, 0);

  if (totalPercentage === 0) {
    return columnsToNormalize;
  }

  const hasStickyColumns = columnsToNormalize.some(
    col => col.sticky === STICKY_POSITION.LEFT || col.sticky === STICKY_POSITION.RIGHT
  );
  const targetTotal = hasStickyColumns ? 92 : 100;
  const scaleFactor = targetTotal / totalPercentage;

  return columnsToNormalize.map(col => {
    if (col.hide) {
      return col;
    }

    const width = col.width || col.calcWidth;
    if (!width?.endsWith('%')) {
      return col;
    }

    const percentage = parsePercentage(width);
    const normalizedPercentage = percentage * scaleFactor;

    if (col.sticky === STICKY_POSITION.LEFT || col.sticky === STICKY_POSITION.RIGHT) {
      return {
        ...col,
        calcWidth: `${normalizedPercentage.toFixed(2)}%`,
      };
    }

    return {
      ...col,
      width: `${normalizedPercentage.toFixed(2)}%`,
    };
  }) as Columns<RowT>;
};
